import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { toProjectDateOnly, toUtcStart } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';
import { useToast } from 'src/shared/ui/toast/useToast';

import { apiCreateActualPayment, apiUpdateActualPayment, apiDeleteActualPayment, apiUpdateContractMilestone, type PaymentStatus } from '../../api/payments.api';

type Props = {
    open: boolean;
    onClose: () => void;

    projectId: string;
    contractId: string;
    milestoneId: string;

    amountPlan: number;
    currentPaid: number;

    payment?: {
        id: string;
        amount: number;
        paidAt: string;
        note?: string | null;
    };
};

export default function ActualPaymentModal({ 
    open, 
    onClose, 
    projectId, 
    contractId, 
    milestoneId, 
    amountPlan,
    currentPaid,
    payment 
}: Props) {
    const qc = useQueryClient();
    const toast = useToast();

    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    const [amount, setAmount] = useState<string>(payment?.amount ? String(payment.amount) : '');
    const [paidAt, setPaidAt] = useState<string>(payment?.paidAt ? toProjectDateOnly(payment.paidAt, zone) : '');
    const [note, setNote] = useState<string>(payment?.note ?? '');

    const [amountError, setAmountError] = useState<string | null>(null);
    const [paidAtError, setPaidAtError] = useState<string | null>(null);

    useEffect(() => {
        if (payment) {
            setAmount(String(payment.amount));
            setPaidAt(payment.paidAt ? toProjectDateOnly(payment.paidAt, zone) : '');
            setNote(payment.note ?? '');
        } else {
            setAmount('');
            setPaidAt('');
            setNote('');
        }

        setAmountError(null);
        setPaidAtError(null);
    }, [payment, open, zone]);

    const invalidate = async () => {
        await qc.invalidateQueries({ queryKey: ['contractDetail', projectId, contractId] });
        await qc.invalidateQueries({ queryKey: ['contractExecution', projectId, contractId] });
        await qc.invalidateQueries({ queryKey: ['contractMilestones', projectId, contractId] });
    };

    const handleStatusSync = async (newAmount: number, isDelete: boolean = false) => {
        // (1) 계산: 삭제 시에는 newAmount를 0으로 취급하고 현재 payment.amount를 뺀다
        let totalAfter = currentPaid;
        if (isDelete && payment) {
            totalAfter = currentPaid - payment.amount;
        } else if (payment) {
            totalAfter = currentPaid - payment.amount + newAmount;
        } else {
            totalAfter = currentPaid + newAmount;
        }

        let nextStatus: PaymentStatus = 'PLANNED';
        if (totalAfter >= amountPlan) nextStatus = 'PAID';
        else if (totalAfter > 0) nextStatus = 'PARTIAL_PAID';

        try {
            await apiUpdateContractMilestone(projectId, contractId, milestoneId, { status: nextStatus });
            await invalidate();
        } catch (e) {
            console.error('Failed to sync status', e);
        }
    };

    const createMut = useMutation({
        mutationFn: () =>
            apiCreateActualPayment(projectId, contractId, milestoneId, {
                amount: Number(amount),
                paidAt: paidAt ? toUtcStart(paidAt, zone) : '',
                note: note || null,
            }),
        onSuccess: async (data: any) => {
            await handleStatusSync(Number(amount));
            await invalidate();
            toast.success('지급 내역을 저장했습니다.', {
                title: '저장 완료',
            });
            onClose();
        },
        onError: (err: any) => {
            toast.error(
                err?.response?.data?.error?.message ??
                    err?.response?.data?.message ??
                    err?.message ??
                    '지급 내역 저장에 실패했습니다.',
                {
                    title: '저장 실패',
                },
            );
        },
    });

    const updateMut = useMutation({
        mutationFn: () =>
            apiUpdateActualPayment(projectId, contractId, payment!.id, {
                amount: Number(amount),
                paidAt: paidAt ? toUtcStart(paidAt, zone) : '',
                note: note || null,
            }),
        onSuccess: async () => {
            await handleStatusSync(Number(amount));
            await invalidate();
            toast.success('지급 내역을 수정했습니다.', {
                title: '수정 완료',
            });
            onClose();
        },
        onError: (err: any) => {
            toast.error(
                err?.response?.data?.error?.message ??
                    err?.response?.data?.message ??
                    err?.message ??
                    '지급 내역 수정에 실패했습니다.',
                {
                    title: '수정 실패',
                },
            );
        },
    });

    const deleteMut = useMutation({
        mutationFn: () => apiDeleteActualPayment(projectId, contractId, payment!.id),
        onSuccess: async () => {
            await handleStatusSync(0, true);
            await invalidate();
            toast.success('지급 내역을 삭제했습니다.', {
                title: '삭제 완료',
            });
            onClose();
        },
        onSettled: () => {
            invalidate();
        },
        onError: (err: any) => {
            toast.error(
                err?.response?.data?.error?.message ??
                    err?.response?.data?.message ??
                    err?.message ??
                    '지급 내역 삭제에 실패했습니다.',
                {
                    title: '삭제 실패',
                },
            );
        },
    });

    if (!open) return null;

    const saving = createMut.isPending || updateMut.isPending;
    const deleting = deleteMut.isPending;

    const handleSave = () => {
        let hasError = false;

        setAmountError(null);
        setPaidAtError(null);

        const numAmount = Number(amount);
        if (!(Number.isFinite(numAmount) && numAmount > 0)) {
            setAmountError('금액은 0보다 커야 합니다.');
            hasError = true;
        }

        // 초과 지급 체크
        const otherPaid = payment ? (currentPaid - payment.amount) : currentPaid;
        if (numAmount + otherPaid > amountPlan) {
            setAmountError(`계획금액(${amountPlan.toLocaleString()})을 초과할 수 없습니다. (현재 잔액: ${(amountPlan - otherPaid).toLocaleString()})`);
            hasError = true;
        }

        if (!paidAt) {
            setPaidAtError('지급일을 입력하세요.');
            hasError = true;
        }

        if (hasError) return;

        if (payment) {
            updateMut.mutate();
        } else {
            createMut.mutate();
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="w-[420px] rounded-2xl bg-white p-5 shadow-lg">
                <div className="mb-4 text-lg font-semibold">{payment ? 'Edit Payment' : 'New Payment'}</div>

                <div className="space-y-3">
                    <div>
                        <label className="text-xs text-gray-500">Amount</label>
                        <input
                            type="number"
                            className={`mt-1 w-full rounded border px-3 py-2 text-sm ${
                                amountError ? 'border-rose-400 bg-rose-50' : ''
                            }`}
                            value={amount}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                                setAmount(e.target.value);
                                if (amountError) setAmountError(null);
                            }}
                        />
                        {amountError ? <div className="mt-1 text-xs text-rose-600">{amountError}</div> : null}
                    </div>

                    <div>
                        <label className="text-xs text-gray-500">Paid Date</label>
                        <input
                            type="date"
                            className={`mt-1 w-full rounded border px-3 py-2 text-sm ${
                                paidAtError ? 'border-rose-400 bg-rose-50' : ''
                            }`}
                            value={paidAt}
                            onChange={(e) => {
                                setPaidAt(e.target.value);
                                if (paidAtError) setPaidAtError(null);
                            }}
                        />
                        {paidAtError ? <div className="mt-1 text-xs text-rose-600">{paidAtError}</div> : null}
                    </div>

                    <div>
                        <label className="text-xs text-gray-500">Note</label>
                        <textarea
                            className="mt-1 w-full rounded border px-3 py-2 text-sm"
                            rows={3}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                        />
                    </div>
                </div>

                <div className="mt-5 flex items-center justify-between">
                    {payment ? (
                        <button
                            className="text-sm text-red-600 hover:underline disabled:opacity-50"
                            disabled={deleting}
                            onClick={() => {
                                if (window.confirm('이 지급 내역을 삭제하시겠습니까?')) {
                                    deleteMut.mutate();
                                }
                            }}
                        >
                            Delete
                        </button>
                    ) : (
                        <span />
                    )}

                    <div className="flex gap-2">
                        <button
                            className="rounded px-3 py-1.5 text-sm border"
                            onClick={onClose}
                            disabled={saving || deleting}
                        >
                            Cancel
                        </button>

                        <button
                            className="rounded px-3 py-1.5 text-sm bg-gray-900 text-white disabled:opacity-50"
                            disabled={saving || deleting}
                            onClick={handleSave}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
